import React, { useCallback, useState } from "react";
import moment from "moment";
import {
  Grid,
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { EnhancedTableHead } from "./index";
import excelIcon from "../../../../assets/images/icons/excel.svg";
import axios from "../../../../axios";

// ✅ Updated Columns
const creditNoteHeadCells = [
  { id: "i", label: "Sl.No" },
  { id: "company", label: "Company Name" },
  { id: "booking_ref", label: "Booking Ref" },
  { id: "date", label: "Attendance Date" },
  { id: "credit", label: "Credit No / Date" },
  { id: "language", label: "Language / Service" },
  { id: "interpreter", label: "Interpreter" },
  { id: "client", label: "Client Name" },
  { id: "amount", label: "Amount" },
  { id: "vat", label: "VAT" },
  { id: "total", label: "Credit Total" },
];

const styles = {
  fontSize: "13px",
  padding: "6px",
};

const CardItem = ({
  active,
  classes,
  data,
  isLoading,
  matches,
  title,
  values,
  hasData,
}) => {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("company");
  const [downloading, setDownloading] = useState(false);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = data.map((n) => n._id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [data],
  );

  const downloadHandler = async () => {
    try {
      setDownloading(true);
      let query = "";

      if (values.companyId) {
        query += `companyId=${values.companyId}`;
      }

      if (values.businessType) {
        query += `${query ? "&" : ""}businessType=${values.businessType}`;
      }

      if (values.fromDate) {
        query += `${query ? "&" : ""}fromDate=${values.fromDate}`;
      }

      if (values.toDate) {
        query += `${query ? "&" : ""}toDate=${values.toDate}`;
      }

      const response = await axios.get(
        `/api/v1/bookings/creditnotereport/download?${query}`,
        {
          responseType: "blob", // 🔥 IMPORTANT
        },
      );

      // ✅ Create file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `CreditNoteReport_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className={active}>
      <CardContent style={{ padding: "8px 6px" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: "6px" }}
        >
          {/* Left: Title */}
          <Grid item xs>
            <Typography variant="body1" noWrap>
              {title}
            </Typography>
          </Grid>

          {/* Right: Excel Icon */}
          {hasData && (
            <Grid item>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    size="medium"
                    cursor="pointer"
                    onClick={downloadHandler}
                    disabled={downloading || data.length === 0}
                  >
                    <img
                      src={excelIcon}
                      alt="excel"
                      style={{ width: 25, height: 25 }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <TableContainer
          className={matches ? classes.container2 : classes.container}
        >
          <Table stickyHeader size="small">
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={creditNoteHeadCells}
            />

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No record
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => {
                  return (
                    <TableRow hover key={row._id || index}>
                      <TableCell style={styles}>{index + 1}</TableCell>

                      {/* Company */}
                      <TableCell style={styles}>
                        <b>
                          {row.businessType === "Private"
                            ? "Private"
                            : row.companyName}
                        </b>
                      </TableCell>

                      {/* Booking Ref */}
                      <TableCell style={styles}>{row.booking_ref}</TableCell>

                      {/* Date */}
                      <TableCell style={styles}>
                        {moment(row.date).format("DD/MM/YYYY")}
                      </TableCell>

                      {/* Credit No / Date */}
                      <TableCell style={styles}>
                        {row.creditNo}
                        <br />
                        <small>
                          {moment(row.creditDate).format("DD/MM/YYYY")}
                        </small>
                      </TableCell>

                      {/* Language / Service */}
                      <TableCell style={styles}>
                        {row.language} / {row.serviceType}
                      </TableCell>

                      {/* Interpreter */}
                      <TableCell style={styles}>{row.interpreter}</TableCell>

                      {/* Client */}
                      <TableCell style={styles}>{row.clientName}</TableCell>

                      {/* Amount */}
                      <TableCell style={styles}>
                        {row.amount?.toFixed(2)}
                      </TableCell>

                      {/* VAT */}
                      <TableCell style={styles}>
                        {row.vatAmount?.toFixed(2)}
                      </TableCell>

                      {/* Credit Total */}
                      <TableCell style={styles}>
                        <strong>{row.creditTotal?.toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default React.memo(CardItem);
